import axios from 'axios';

// In-memory cache to avoid repeated API calls during a session
const wikiCache = new Map();
const CACHE_TTL = 30 * 60 * 1000; // 30 minutes

export const wikipediaService = {
  baseURL: 'https://en.wikipedia.org/w/api.php',

  /**
   * Get a real Wikipedia portrait image for a named individual.
   * Uses multiple strategies to find the best image.
   */
  async getIndividualImage(name) {
    const cacheKey = `img_${name}`;
    const cached = wikiCache.get(cacheKey);
    if (cached && Date.now() - cached.ts < CACHE_TTL) {
      return cached.data;
    }

    try {
      // Strategy 1: Direct page lookup with pageimages
      const imageUrl = await this._fetchPageImage(name);
      if (imageUrl) {
        wikiCache.set(cacheKey, { data: imageUrl, ts: Date.now() });
        return imageUrl;
      }

      // Strategy 2: Search for the person and get first result's image
      const searchResults = await this._searchPages(name);
      for (const title of searchResults) {
        const img = await this._fetchPageImage(title);
        if (img) {
          wikiCache.set(cacheKey, { data: img, ts: Date.now() });
          return img;
        }
      }

      return null;
    } catch (error) {
      console.error(`Wikipedia image fetch failed for "${name}":`, error.message);
      return null;
    }
  },

  /**
   * Get comprehensive individual info from Wikipedia.
   * Returns structured data including image, net worth, bio, etc.
   */
  async getIndividualInfo(name) {
    const cacheKey = `info_${name}`;
    const cached = wikiCache.get(cacheKey);
    if (cached && Date.now() - cached.ts < CACHE_TTL) {
      return cached.data;
    }

    try {
      // Step 1: Find the right Wikipedia page
      const pageTitle = await this._findBestPage(name, 'person');
      if (!pageTitle) return null;

      // Step 2: Get full page data (extract, image, infobox)
      const pageData = await this._getFullPageData(pageTitle);
      if (!pageData) return null;

      // Step 3: Parse and structure the data
      const result = this._parseIndividualData(pageData, pageTitle);
      
      wikiCache.set(cacheKey, { data: result, ts: Date.now() });
      return result;
    } catch (error) {
      console.error(`Wikipedia info fetch failed for "${name}":`, error.message);
      return null;
    }
  },

  /**
   * Get company info from Wikipedia.
   */
  async getCompanyInfo(companyName) {
    const cacheKey = `company_${companyName}`;
    const cached = wikiCache.get(cacheKey);
    if (cached && Date.now() - cached.ts < CACHE_TTL) {
      return cached.data;
    }

    try {
      const pageTitle = await this._findBestPage(companyName, 'company');
      if (!pageTitle) return null;

      const pageData = await this._getFullPageData(pageTitle);
      if (!pageData) return null;

      const result = this._parseCompanyData(pageData, pageTitle);
      
      wikiCache.set(cacheKey, { data: result, ts: Date.now() });
      return result;
    } catch (error) {
      console.error(`Wikipedia company info fetch failed for "${companyName}":`, error.message);
      return null;
    }
  },

  /**
   * Batch-fetch individual info for multiple people.
   * Returns array of results (null for failures).
   */
  async getMultipleIndividualsInfo(names) {
    const results = [];

    // Process in batches of 3 to avoid rate limiting
    for (let i = 0; i < names.length; i += 3) {
      const batch = names.slice(i, i + 3);
      const batchPromises = batch.map(name => this.getIndividualInfo(name));

      try {
        const batchResults = await Promise.allSettled(batchPromises);
        results.push(
          ...batchResults.map(r => (r.status === 'fulfilled' ? r.value : null))
        );

        // Rate-limit delay between batches
        if (i + 3 < names.length) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      } catch {
        results.push(...batch.map(() => null));
      }
    }

    return results;
  },

  /**
   * Batch-fetch just images for multiple people. Faster than full info.
   */
  async getMultipleImages(names) {
    const results = {};

    for (let i = 0; i < names.length; i += 5) {
      const batch = names.slice(i, i + 5);
      const batchPromises = batch.map(async (name) => {
        const url = await this.getIndividualImage(name);
        return { name, url };
      });

      try {
        const batchResults = await Promise.allSettled(batchPromises);
        for (const r of batchResults) {
          if (r.status === 'fulfilled' && r.value.url) {
            results[r.value.name] = r.value.url;
          }
        }

        if (i + 5 < names.length) {
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      } catch {
        // continue
      }
    }

    return results;
  },

  // ───────────────────────────────────────────────
  // Private helpers
  // ───────────────────────────────────────────────

  /**
   * Search Wikipedia and return page titles.
   */
  async _searchPages(query, limit = 5) {
    try {
      const response = await axios.get(this.baseURL, {
        params: {
          action: 'query',
          list: 'search',
          srsearch: query,
          format: 'json',
          origin: '*',
          srlimit: limit,
        },
      });
      return response.data?.query?.search?.map(r => r.title) || [];
    } catch {
      return [];
    }
  },

  /**
   * Find the best matching Wikipedia page for a person or company.
   */
  async _findBestPage(name, type = 'person') {
    // Try direct name first (most reliable for famous people)
    const directResults = await this._searchPages(name, 3);
    if (directResults.length > 0) {
      // Check if the first result is a good match
      const firstName = name.split(' ')[0].toLowerCase();
      const lastName = name.split(' ').slice(-1)[0].toLowerCase();
      
      for (const title of directResults) {
        const lowerTitle = title.toLowerCase();
        if (lowerTitle.includes(firstName) && lowerTitle.includes(lastName)) {
          return title;
        }
      }
      // If no exact match, return first result
      return directResults[0];
    }

    // Fallback: add context to search
    const suffix = type === 'person' 
      ? 'businessman entrepreneur' 
      : 'company corporation';
    const contextResults = await this._searchPages(`${name} ${suffix}`, 3);
    return contextResults[0] || null;
  },

  /**
   * Fetch the page image URL directly using the pageimages API.
   */
  async _fetchPageImage(pageTitle) {
    try {
      const response = await axios.get(this.baseURL, {
        params: {
          action: 'query',
          titles: pageTitle,
          prop: 'pageimages',
          piprop: 'original',
          format: 'json',
          origin: '*',
        },
      });

      const pages = response.data?.query?.pages;
      if (!pages) return null;

      const pageId = Object.keys(pages)[0];
      const page = pages[pageId];

      // The 'original' property contains { source, width, height }
      return page?.original?.source || null;
    } catch {
      return null;
    }
  },

  /**
   * Get comprehensive page data: extract, image, and raw wikitext.
   */
  async _getFullPageData(pageTitle) {
    try {
      const response = await axios.get(this.baseURL, {
        params: {
          action: 'query',
          titles: pageTitle,
          prop: 'extracts|pageimages|revisions',
          exintro: true,
          explaintext: true,
          piprop: 'original',
          rvprop: 'content',
          rvslots: 'main',
          format: 'json',
          origin: '*',
        },
      });

      const pages = response.data?.query?.pages;
      if (!pages) return null;

      const pageId = Object.keys(pages)[0];
      if (pageId === '-1') return null; // Page not found

      return pages[pageId];
    } catch {
      return null;
    }
  },

  /**
   * Parse individual data from Wikipedia page content.
   */
  _parseIndividualData(pageData, title) {
    const extract = pageData?.extract || '';
    const wikitext = pageData?.revisions?.[0]?.slots?.main?.['*'] 
                  || pageData?.revisions?.[0]?.['*'] 
                  || '';
    const imageUrl = pageData?.original?.source || null;

    // Extract net worth from infobox or text
    const netWorth = this._extractNetWorth(extract, wikitext);

    // Extract birth year
    const birthYear = this._extractBirthYear(extract, wikitext);

    // Extract position/title
    const position = this._extractPosition(extract);

    // Extract associated companies
    const companies = this._extractCompanies(extract, wikitext);

    // Extract political leaning indicators
    const politicalLeaning = this._estimatePoliticalLeaning(extract, wikitext);

    // Extract location
    const location = this._extractLocation(extract, wikitext);

    // Extract education
    const education = this._extractEducation(extract, wikitext);

    return {
      name: title,
      imageUrl,
      bio: extract.substring(0, 600),
      netWorth,
      birthYear,
      position,
      companies,
      primaryCompany: companies[0] || null,
      politicalLeaning,
      location,
      education,
      wikipediaUrl: `https://en.wikipedia.org/wiki/${encodeURIComponent(title.replace(/ /g, '_'))}`,
    };
  },

  /**
   * Parse company data from Wikipedia page content.
   */
  _parseCompanyData(pageData, title) {
    const extract = pageData?.extract || '';
    const wikitext = pageData?.revisions?.[0]?.slots?.main?.['*'] 
                  || pageData?.revisions?.[0]?.['*'] 
                  || '';
    const imageUrl = pageData?.original?.source || null;

    return {
      name: title,
      logoUrl: imageUrl,
      description: extract.substring(0, 500),
      industry: this._extractIndustry(extract),
      foundedYear: this._extractFoundedYear(extract, wikitext),
      headquarters: this._extractHeadquarters(extract, wikitext),
      employees: this._extractEmployees(extract, wikitext),
      revenue: this._extractRevenue(wikitext),
      website: this._extractWebsite(wikitext),
      wikipediaUrl: `https://en.wikipedia.org/wiki/${encodeURIComponent(title.replace(/ /g, '_'))}`,
    };
  },

  // ─── Data extraction helpers ───

  _extractNetWorth(extract, wikitext) {
    const combined = extract + ' ' + wikitext;
    const patterns = [
      // Infobox pattern: | net_worth = US$XXX billion
      /net[_ ]worth\s*=\s*(?:US)?\$\s*(\d{1,4}(?:\.\d+)?)\s*billion/i,
      // Text pattern
      /net worth[^$]*?\$\s*(\d{1,4}(?:\.\d+)?)\s*billion/i,
      /worth[^$]*?\$\s*(\d{1,4}(?:\.\d+)?)\s*billion/i,
      /(\d{1,4}(?:\.\d+)?)\s*billion[^a-z]*?net worth/i,
      // Million patterns
      /net worth[^$]*?\$\s*(\d{1,6}(?:\.\d+)?)\s*million/i,
    ];

    for (const pattern of patterns) {
      const match = combined.match(pattern);
      if (match) {
        const value = parseFloat(match[1]);
        const isBillion = pattern.source.includes('billion');
        return isBillion ? value * 1_000_000_000 : value * 1_000_000;
      }
    }

    return null;
  },

  _extractBirthYear(extract, wikitext) {
    // Look for birth_date in infobox
    const infoboxMatch = wikitext.match(/birth_date\s*=.*?(\d{4})/);
    if (infoboxMatch) return parseInt(infoboxMatch[1]);

    // Look for "born" in text
    const textMatch = extract.match(/born[^)]*?(\d{4})/i);
    if (textMatch) return parseInt(textMatch[1]);

    return null;
  },

  _extractPosition(extract) {
    const patterns = [
      /(?:is|was)\s+(?:the\s+)?(?:co-)?(?:CEO|Chief Executive Officer|Chairman|President|Founder|Executive Chairman)\s+(?:of|and\s+\w+\s+of)\s+([^,.]+)/i,
      /(?:CEO|Chairman|President|Founder)\s+of\s+([^,.]+)/i,
    ];

    for (const pattern of patterns) {
      const match = extract.match(pattern);
      if (match) return match[0].trim();
    }

    // Try to get first sentence as description
    const firstSentence = extract.split('.')[0];
    if (firstSentence && firstSentence.length < 200) {
      // Extract role from first sentence
      const roleMatch = firstSentence.match(/(?:is|was)\s+(?:an?\s+)?([^,.]+)/i);
      if (roleMatch) return roleMatch[1].trim();
    }

    return 'Business Executive';
  },

  _extractCompanies(extract, wikitext) {
    const companies = new Set();
    const patterns = [
      /(?:CEO|founder|chairman|president|executive chairman)\s+(?:of|and\s+\w+\s+of)\s+([A-Z][a-zA-Z0-9\s&'.]+?)(?:\s*[,.\)]|$)/gi,
      /(?:founded|co-founded|started)\s+([A-Z][a-zA-Z0-9\s&'.]+?)(?:\s*[,.\)]|$|\s+in\s)/gi,
    ];

    for (const pattern of patterns) {
      let match;
      while ((match = pattern.exec(extract)) !== null && companies.size < 8) {
        const company = match[1].trim().replace(/\s+/g, ' ');
        if (company.length > 2 && company.length < 50) {
          companies.add(company);
        }
      }
    }

    return Array.from(companies).slice(0, 5);
  },

  _estimatePoliticalLeaning(extract, wikitext) {
    const text = (extract + ' ' + wikitext).toLowerCase();
    
    const conservativeSignals = [
      'republican', 'conservative', 'right-wing', 'libertarian',
      'trump', 'gop', 'free market', 'deregulation',
    ];
    const liberalSignals = [
      'democrat', 'progressive', 'liberal', 'left-wing',
      'biden', 'obama', 'social justice', 'climate change',
      'environmental', 'diversity',
    ];

    let conservativeScore = 0;
    let liberalScore = 0;

    for (const term of conservativeSignals) {
      if (text.includes(term)) conservativeScore++;
    }
    for (const term of liberalSignals) {
      if (text.includes(term)) liberalScore++;
    }

    if (conservativeScore > liberalScore + 1) return 'conservative';
    if (liberalScore > conservativeScore + 1) return 'liberal';
    return 'centrist';
  },

  _extractLocation(extract, wikitext) {
    const patterns = [
      /(?:lives|resides|based)\s+in\s+([^,.]+)/i,
      /residence\s*=\s*([^\n|]+)/i,
    ];

    for (const pattern of patterns) {
      const match = (extract + ' ' + wikitext).match(pattern);
      if (match) return match[1].trim();
    }
    return null;
  },

  _extractEducation(extract, wikitext) {
    const patterns = [
      /(?:graduated|studied|attended|educated)\s+(?:from|at)\s+([^,.]+)/i,
      /alma[_ ]mater\s*=\s*([^\n|]+)/i,
    ];

    for (const pattern of patterns) {
      const match = (extract + ' ' + wikitext).match(pattern);
      if (match) return match[1].trim().replace(/\[\[|\]\]/g, '');
    }
    return null;
  },

  _extractIndustry(extract) {
    const industries = {
      'Technology': ['software', 'technology', 'tech', 'computer', 'internet', 'digital', 'AI', 'artificial intelligence'],
      'Finance': ['bank', 'financial', 'payment', 'fintech', 'investment', 'insurance'],
      'Retail': ['retail', 'store', 'marketplace', 'shopping', 'e-commerce'],
      'Automotive': ['automotive', 'car', 'vehicle', 'electric vehicle'],
      'Food & Beverage': ['food', 'beverage', 'restaurant', 'coffee'],
      'Fashion & Luxury': ['fashion', 'luxury', 'clothing', 'apparel'],
      'Media & Entertainment': ['media', 'entertainment', 'streaming', 'film', 'music'],
      'Healthcare': ['health', 'pharma', 'medical', 'biotech'],
      'Energy': ['energy', 'oil', 'gas', 'renewable', 'solar'],
      'Aerospace': ['aerospace', 'space', 'aviation', 'rocket'],
    };

    const lower = extract.toLowerCase();
    for (const [industry, keywords] of Object.entries(industries)) {
      if (keywords.some(kw => lower.includes(kw))) {
        return industry;
      }
    }
    return 'Conglomerate';
  },

  _extractFoundedYear(extract, wikitext) {
    const match = (extract + ' ' + wikitext).match(
      /(?:founded|established|incorporated)\s*(?:in\s*)?(\d{4})/i
    );
    return match ? parseInt(match[1]) : null;
  },

  _extractHeadquarters(extract, wikitext) {
    const patterns = [
      /headquartered\s+in\s+([^,.]+)/i,
      /headquarters?\s*=\s*([^\n|]+)/i,
      /based\s+in\s+([^,.]+)/i,
    ];
    for (const pattern of patterns) {
      const match = (extract + ' ' + wikitext).match(pattern);
      if (match) return match[1].trim().replace(/\[\[|\]\]/g, '');
    }
    return null;
  },

  _extractEmployees(extract, wikitext) {
    const match = (extract + ' ' + wikitext).match(
      /(?:num[_ ]employees|employees)\s*=?\s*(?:approximately\s+)?(\d{1,3}(?:[,. ]\d{3})*)/i
    );
    return match ? parseInt(match[1].replace(/[,. ]/g, '')) : null;
  },

  _extractRevenue(wikitext) {
    const patterns = [
      /revenue\s*=\s*(?:US)?\$?\s*(\d{1,4}(?:\.\d+)?)\s*billion/i,
      /revenue\s*=\s*(?:US)?\$?\s*(\d{1,6}(?:\.\d+)?)\s*million/i,
    ];
    for (const pattern of patterns) {
      const match = wikitext.match(pattern);
      if (match) {
        const val = parseFloat(match[1]);
        return pattern.source.includes('billion') ? val * 1e9 : val * 1e6;
      }
    }
    return null;
  },

  _extractWebsite(wikitext) {
    const match = wikitext.match(/(?:website|url)\s*=\s*\{?\s*(https?:\/\/[^\s|}]+)/i);
    return match ? match[1].trim() : null;
  },
};